import React, { useState, useEffect } from "react";
import { HashRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import Home from "./Home";
import Services from "./Services";
import OurStory from "./Ourstory";
import Booking from "./Booking";
import "./index.css";
import emailjs from "@emailjs/browser";

emailjs.init("JjDr-XBM40wFmJ4NQ");

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// New component to handle current page navigation
function NavLink({ to, children, className, onClick }) {
  const location = useLocation();

  const handleClick = (e) => {
    // If we're already on this page, prevent default navigation and just scroll to top
    if (location.pathname === to) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    // Call the onClick handler passed from parent (for closing mobile menu)
    if (onClick) {
      onClick();
    }
  };

  return (
    <Link to={to} onClick={handleClick} className={className}>
      {children}
    </Link>
  );
}

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Add function to close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <Router>
      <ScrollToTop />

      {/* <Header /> */}
      <div className="app-container">
        <header className={`sticky-header ${isScrolled ? "scrolled" : ""}`}>
          <div className="site-header">
            <div className="site-header-logo">
              <NavLink to="/" onClick={closeMobileMenu}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/3.svg`}
                  alt="All Star Logistics"
                  className="logo-image"
                />
                <span className="logo-text">All Star Logistics Partners</span>
              </NavLink>
            </div>

            {/* Mobile menu button */}
            <button
              className={`mobile-menu-button ${isMobileMenuOpen ? "active" : ""}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <nav className={isMobileMenuOpen ? "active" : ""}>
              <ul>
                <li>
                  <NavLink to="/" onClick={closeMobileMenu}>
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/ourstory" onClick={closeMobileMenu}>
                    Our Story
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/services" onClick={closeMobileMenu}>
                    Services
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/booking" className="booking-highlight" onClick={closeMobileMenu}>
                    Get a Quote
                  </NavLink>
                </li>
              </ul>
            </nav>
          </div>
        </header>

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/ourstory" element={<OurStory />} />
            <Route path="/booking" element={<Booking />} />
          </Routes>
        </main>

        {/* Footer Section */}
        <footer className="footer">
          <div className="footer-content">
            {/* Company Info Section */}
            <div className="footer-section company-info">
              <h3>All Star Logistics Partners</h3>
              <p>Your trusted partner in dumpster rental services since 2013.</p>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:+12563666679"> (256) 366-6679</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto: sales@allstarrecycling.net">sales@allstarrecycling.net</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <p>
                    200 Auburn St. <br />
                    Town Creek, AL, 35672
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="footer-section quick-links">
              <h4>Quick Links</h4>
              <ul>
                <li>
                  <NavLink to="/">Home</NavLink>
                </li>
                <li>
                  <NavLink to="/ourstory">Our Story</NavLink>
                </li>
                <li>
                  <NavLink to="/services">Services</NavLink>
                </li>
                <li>
                  <NavLink to="/booking">Get a Quote</NavLink>
                </li>
              </ul>
            </div>

            {/* Business Hours Section */}
            <div className="footer-section hours">
              <h4>Business Hours</h4>
              <ul className="hours-list">
                <li>
                  <span>Monday - Friday:</span>
                  <span>7:00 AM - 4:00 PM</span>
                </li>
                <li>
                  <span>Saturday:</span>
                  <span>*On Demand*</span>
                </li>
                <li>
                  <span>Sunday:</span>
                  <span>*On Demand*</span>
                </li>
              </ul>
            </div>

            {/* Connect Section */}
            <div className="footer-section connect">
              <h4>Connect With Us</h4>
              <div className="social-links">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                >
                  <i className="fab fa-twitter"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <div className="copyright">
                <p>
                  &copy; {new Date().getFullYear()} All Star Logistics Partners. All rights
                  reserved.
                </p>
              </div>
              <div className="legal-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-service">Terms of Service</a>
                <a href="/accessibility">Accessibility</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
